import {
  useCustomAttributionControllerCreateModelMutation,
  useCustomAttributionControllerBulkUpdateWeightsMutation,
} from "~/ui-rtk/api/customAttributionApi"
import {
  AdChannel,
  InitiativeType,
  MetaObjective,
  OptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"

import Button from "~/ui-rtk/components/ui/controls/Button"
import { Formik, Form } from "formik"
import { motion, AnimatePresence } from "framer-motion"
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { ConfigurationStep } from "./ConfigurationStep"
import { WeightTabs } from "./WeightTabs"
import { useState } from "react"
import type { CustomInitiativeTypeMeta } from "~/ui-rtk/constants/initiative-types"

import * as Yup from "yup"
import {
  resolveWeightSourceForApi,
  weightSourceForForm,
} from "../constants/attribution"

interface CreateModelModalProps {
  isOpen: boolean
  onClose: () => void
  initiativeType: string
  defaultActiveTab?: ModelType | null
  customTypes?: CustomInitiativeTypeMeta[]
  onSuccess?: () => void
  adsetData?: Record<string, string | undefined>[]
  existingModel?: {
    id: string | null
    adChannel: AdChannel
    adChannelObjective?: MetaObjective
    adChannelOptimizationGoal?: OptimizationGoal
    weights: Array<{
      id: string
      modelType: ModelType
      source: string
      metric: string
      weight: number
    }>
  }
}

interface WeightFormValues {
  adChannel: AdChannel | null
  adChannelObjective: MetaObjective | null
  adChannelOptimizationGoal: OptimizationGoal | null
  weights: Array<{
    id?: string
    modelType: ModelType
    source: string
    metric: string
    weight: number
  }>
  deletedWeightIds: string[]
}

// RTK Query surfaces server errors as { data: { message } } where message may be
// a string or an array of validation strings. Fall back to a generic message.
function getApiErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string | string[] } }).data
    if (data?.message) {
      return Array.isArray(data.message)
        ? data.message.join(", ")
        : data.message
    }
  }
  return "Failed to save the model. Please try again."
}

const validationSchema = Yup.object().shape({
  adChannel: Yup.string().required("Channel is required"),
  adChannelObjective: Yup.string().optional().nullable(),
  adChannelOptimizationGoal: Yup.string().required("Goal is required"),
  weights: Yup.array().of(
    Yup.object().shape({
      source: Yup.string().nullable(),
      metric: Yup.string().when("source", {
        is: (val: string) => val && val.length > 0,
        then: schema => schema.required("Metric is required"),
        otherwise: schema => schema.nullable(),
      }),
      weight: Yup.number().when("source", {
        is: (val: string) => val && val.length > 0,
        then: schema =>
          schema
            .required("Weight is required")
            .min(0.01, "Weight must be greater than 0"),
        otherwise: schema => schema.nullable(),
      }),
    }),
  ),
})

export function CreateModelModal({
  isOpen,
  onClose,
  initiativeType,
  defaultActiveTab,
  customTypes,
  onSuccess,
  adsetData,
  existingModel,
}: CreateModelModalProps) {
  const [createModel] = useCustomAttributionControllerCreateModelMutation()
  const [bulkUpdateWeights] =
    useCustomAttributionControllerBulkUpdateWeightsMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const initialValues: WeightFormValues = {
    adChannel: existingModel?.adChannel || null,
    adChannelObjective: existingModel?.adChannelObjective || null,
    adChannelOptimizationGoal: existingModel?.adChannelOptimizationGoal || null,
    weights:
      existingModel?.weights?.map(w => ({
        id: w.id,
        modelType: w.modelType,
        source: weightSourceForForm(w.source, w.metric),
        metric: w.metric,
        weight: w.weight,
      })) || [],
    deletedWeightIds: [],
  }

  const handleSubmit = async (values: WeightFormValues) => {
    // Filter out empty weights (weights without a source)
    const filledWeights = values.weights.filter(
      w => w.source && w.source.length > 0,
    )

    // Validate that at least one weight is configured
    if (filledWeights.length === 0) {
      setErrorMessage(
        "Please configure at least one attribution weight before saving the model.",
      )
      return
    }

    // Clear any previous error messages and set loading
    setErrorMessage(null)
    setIsSaving(true)

    try {
      if (existingModel && existingModel.id) {
        // Update existing model weights using bulk update
        const modelId = existingModel.id
        await bulkUpdateWeights({
          modelId,
          weights: filledWeights.map(w => ({
            id: w.id,
            modelId,
            modelType: w.modelType,
            source: resolveWeightSourceForApi(w.source),
            metric: w.metric,
            weight: w.weight,
          })),
          deletedWeightIds: values.deletedWeightIds,
        }).unwrap()
      } else {
        // Create new model first
        if (!values.adChannel) {
          throw new Error("Ad channel is required")
        }

        // Create the model and its weights in a single atomic request so a
        // partial failure can't leave an orphaned model behind (and a retry
        // can't create a duplicate).
        await createModel({
          createCustomAttributionModelDto: {
            adChannel: values.adChannel,
            initiativeType: initiativeType as InitiativeType,
            adChannelObjective: values.adChannelObjective || undefined,
            adChannelOptimizationGoal:
              values.adChannelOptimizationGoal || undefined,
            weights: filledWeights.map(w => ({
              modelType: w.modelType,
              source: resolveWeightSourceForApi(w.source),
              metric: w.metric,
              weight: w.weight,
            })),
          },
        }).unwrap()
      }

      // Always call success callback and close modal on successful save
      if (onSuccess) {
        try {
          onSuccess()
        } catch (callbackError) {
          console.error("Error in onSuccess callback:", callbackError)
        }
      }
      onClose()
    } catch (error: unknown) {
      console.error("Failed to save model:", error)
      // Surface the error in the modal and keep it open so the user can retry.
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) {
    // Reset error message when modal closes
    if (errorMessage) {
      setErrorMessage(null)
    }
    return null
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-center pt-[15vh]">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-10 bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl max-h-[75vh] overflow-y-auto w-[700px] h-fit">
          {/* Loading Overlay */}
          {isSaving && (
            <div className="absolute inset-0 bg-system-blue-800/20 z-50 flex items-center justify-center rounded-system-xl">
              <div className="bg-system-blue-800 border border-system-blue-600/50 rounded-lg px-6 py-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-system-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-white font-medium">
                    Saving changes...
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* Header */}
          <div className="bg-system-blue-700 border-b border-system-blue-600/50 px-system-md py-system-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {initiativeType.replace(/_/g, " ").toUpperCase()}
              </h2>
              <button
                onClick={onClose}
                className="text-system-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-system-blue-600/20"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, setFieldValue }) => {
                // Check if configuration is complete
                const configComplete =
                  values.adChannel &&
                  ((values.adChannel === AdChannel.META &&
                    values.adChannelObjective &&
                    values.adChannelOptimizationGoal) ||
                    ((values.adChannel === AdChannel.TIKTOK ||
                      values.adChannel === AdChannel.PINTEREST ||
                      values.adChannel === AdChannel.GOOGLE_ADS ||
                      values.adChannel === AdChannel.YOUTUBE) &&
                      values.adChannelOptimizationGoal))

                return (
                  <Form className="space-y-6">
                    {/* Configuration Section */}
                    <ConfigurationStep
                      adChannel={values.adChannel}
                      adChannelObjective={values.adChannelObjective}
                      adChannelOptimizationGoal={
                        values.adChannelOptimizationGoal
                      }
                      onChannelSelect={channel => {
                        void setFieldValue("adChannel", channel)
                        void setFieldValue("adChannelObjective", "")
                        void setFieldValue("adChannelOptimizationGoal", "")
                      }}
                      onObjectiveSelect={objective => {
                        void setFieldValue("adChannelObjective", objective)
                        void setFieldValue("adChannelOptimizationGoal", "")
                      }}
                      onGoalSelect={goal => {
                        void setFieldValue("adChannelOptimizationGoal", goal)
                      }}
                      onBack={() => {
                        // Go to previous step based on channel type
                        if (
                          values.adChannel === AdChannel.META ||
                          values.adChannel === AdChannel.TIKTOK ||
                          values.adChannel === AdChannel.PINTEREST ||
                          values.adChannel === AdChannel.GOOGLE_ADS ||
                          values.adChannel === AdChannel.YOUTUBE
                        ) {
                          // For Meta, go back to goal selection (clear only goal)
                          void setFieldValue("adChannelOptimizationGoal", "")
                        } else {
                          // For TikTok / Pinterest, go back to channel selection (clear goal since they skip objectives)
                          void setFieldValue("adChannelOptimizationGoal", "")
                        }
                      }}
                      isEditMode={!!existingModel}
                      errors={{
                        adChannel: errors.adChannel,
                        adChannelObjective: errors.adChannelObjective,
                        adChannelOptimizationGoal:
                          errors.adChannelOptimizationGoal,
                      }}
                      touched={{
                        adChannel: touched.adChannel,
                        adChannelObjective: touched.adChannelObjective,
                        adChannelOptimizationGoal:
                          touched.adChannelOptimizationGoal,
                      }}
                      adsetData={adsetData}
                    />

                    {/* Weights Section - Only visible when configuration is complete */}
                    <AnimatePresence>
                      {configComplete && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                          }}
                          exit={{
                            opacity: 0,
                          }}
                          className="px-system-md pb-system-md overflow-hidden"
                        >
                          <div className="mb-system-sm">
                            <h3 className="text-lg font-semibold text-white mb-1">
                              Weights Configuration
                            </h3>
                            <p className="text-base text-system-gray-400">
                              Configure attribution weights for different model
                              types
                            </p>
                          </div>
                          <div className="h-[500px]">
                            <WeightTabs
                              values={values}
                              defaultActiveTab={defaultActiveTab}
                              initiativeType={initiativeType}
                              customTypes={customTypes}
                              onWeightChange={() => {
                                // Clear error message when user starts configuring weights
                                if (errorMessage) {
                                  setErrorMessage(null)
                                }
                              }}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Error Message */}
                    {errorMessage && (
                      <div className="px-system-md">
                        <div className="flex items-center gap-2 p-system-sm bg-system-error-500/10 border border-system-error-500/30 rounded-lg">
                          <ExclamationTriangleIcon className="w-5 h-5 text-system-error-400 flex-shrink-0" />
                          <span className="text-sm text-system-error-300">
                            {errorMessage}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Submit Buttons */}
                    <div className="flex justify-start space-x-2 px-system-md py-system-sm border-t border-system-blue-600/30 bg-system-blue-600/5">
                      <Button
                        type="button"
                        variant={{ variant: "text" }}
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-system-md py-system-xs text-base"
                      >
                        Cancel
                      </Button>
                      {configComplete && (
                        <Button
                          type="submit"
                          variant={{ variant: "solid", color: "pink" }}
                          disabled={!configComplete || isSaving}
                          className="px-system-md py-system-xs text-base text-white"
                        >
                          {(() => {
                            if (isSaving) return "Saving..."
                            return existingModel
                              ? "Save Changes"
                              : "Create Model"
                          })()}
                        </Button>
                      )}
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>
    </AnimatePresence>
  )
}
